import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Home as HomeIcon, Truck, Sparkles, KeyRound, BedDouble,
  DoorOpen, Tag, Brush, CalendarClock, ArrowRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";

const HERO_IMG = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=2000&q=80";

const SERVICES = [
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Office Cleaning",
    text: "Scheduled professional cleaning for offices, workplaces, and commercial facilities. We handle dusting, sanitization, floors, restrooms, breakrooms, and trash — on a recurring schedule that fits your operation. A consistently clean workplace reflects well on your business and supports your team.",
  },
  {
    id: "residential",
    icon: HomeIcon,
    title: "Residential Cleaning",
    text: "Professional housekeeping for homes, apartments, and condominiums. Whether you need a one-time service or a recurring schedule, we deliver thorough, dependable cleaning that gives you back your time.",
  },
  {
    id: "move",
    icon: Truck,
    title: "Move-In / Move-Out Cleaning",
    text: "A thorough, top-to-bottom cleaning of a property before a new occupant arrives or after a current one departs. We cover every surface, appliance, cabinet, baseboard, and floor to leave the property truly ready for occupancy.",
  },
  {
    id: "deep",
    icon: Sparkles,
    title: "Deep Cleaning",
    text: "A comprehensive, detailed service that goes beyond standard maintenance — including oven and refrigerator interiors, cabinet interiors, ceiling fans, light fixtures, baseboards, wall washing, and window sills. Perfect for seasonal resets, pre-sale prep, or any property that needs a full refresh.",
  },
  {
    id: "property",
    icon: KeyRound,
    title: "Property Management Cleaning",
    text: "Ongoing and turnover cleaning services for property management companies and landlords. We handle unit turnovers, post-tenant cleaning, move-in preparation, and recurring portfolio maintenance — reliably, and without constant supervision.",
  },
  {
    id: "airbnb",
    icon: BedDouble,
    title: "Airbnb / VRBO Cleaning",
    text: "Specialized turnover cleaning for short-term rental properties. We reset linens, sanitize high-touch surfaces, deep clean kitchens and bathrooms, and conduct a final walkthrough before each guest arrives. Your reviews depend on it — so does your income.",
  },
  {
    id: "apartment",
    icon: DoorOpen,
    title: "Apartment Turnover Cleaning",
    text: "Full cleaning of vacant apartment units to restore them to rental-ready condition. We reduce vacancy time and help landlords present units that attract quality tenants quickly.",
  },
  {
    id: "prep",
    icon: Tag,
    title: "Property Preparation for Sale or Rent",
    text: "Pre-listing and pre-marketing cleaning for homeowners, investors, and real estate professionals. A thoroughly cleaned property photographs better, shows more impressively, and often sells or rents faster.",
  },
  {
    id: "floors",
    icon: Brush,
    title: "Floor Care",
    text: "Professional floor cleaning for carpets, tile, hardwood, and hard surfaces — including vacuuming, sweeping, mopping, and tile floor sanitization.",
  },
  {
    id: "recurring",
    icon: CalendarClock,
    title: "Recurring Cleaning Plans",
    text: "Weekly, bi-weekly, or monthly scheduled cleaning for homes, offices, and rental properties. Consistent, familiar service with a team that knows your property and delivers the same results every time.",
  },
];

const SERVICE_AREAS = [
  "Residential Homes",
  "Apartments & Condominiums",
  "Commercial Offices",
  "Rental Properties",
  "Vacation Rentals",
  "Property Management Portfolios",
  "Real Estate Pre-Listing Properties",
  "Short-Term Rental Properties (Airbnb / VRBO)",
];

export default function Services() {
  useEffect(() => {
    document.title = "Cleaning Services | Commercial, Residential & Airbnb | Eco Ready Solutions";
  }, []);

  return (
    <div data-testid="page-services">
      <PageHero
        eyebrow="What We Do"
        title="Professional Cleaning Services"
        subtitle="Commercial, Residential, Rental & Property Management — All Under One Roof"
        image={HERO_IMG}
      />

      {/* INTRO */}
      <section className="eco-section bg-white">
        <div className="eco-container max-w-3xl text-center mx-auto">
          <span className="eco-eyebrow">Our Range</span>
          <p className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1A1A1A]">
            Eco Ready Solutions, LLC offers a comprehensive range of professional cleaning services for commercial and
            residential properties throughout Metro Detroit and Oakland County. Every service is delivered with the
            attention to detail and reliability that comes from over 30 years of professional experience.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="eco-section pt-0 bg-white">
        <div className="eco-container">
          <div className="grid md:grid-cols-2 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <article key={s.id} id={s.id} data-testid={`service-card-${s.id}`} className="eco-card flex flex-col">
                  <div className="flex items-start gap-4">
                    <span className="eco-icon-wrap shrink-0"><Icon size={22} /></span>
                    <div>
                      <h3 className="eco-h3">{s.title}</h3>
                      <p className="mt-3 text-[#555555] text-[15.5px] leading-[1.7]">{s.text}</p>
                    </div>
                  </div>
                  <div className="mt-5 pl-[64px]">
                    <Link to="/contact" className="eco-link-arrow">Get a Quote <ArrowRight size={15} /></Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container">
          <div className="max-w-2xl">
            <span className="eco-eyebrow">Service Area</span>
            <h2 className="eco-h2 mt-3">Where We Work</h2>
          </div>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {SERVICE_AREAS.map((a) => (
              <li key={a} className="flex items-start gap-3 text-[16px] text-[#1A1A1A]">
                <span className="mt-2 w-1.5 h-1.5 rounded-full" style={{ background: "#2D6A4F" }} />
                {a}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-[15.5px] text-[#555555]">
            Serving <strong className="text-[#1A1A1A]">Royal Oak, Metro Detroit, Oakland County</strong>, and surrounding areas.
          </p>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="eco-section text-white" style={{ background: "#2D6A4F" }}>
        <div className="eco-container grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <h2 className="eco-h2 text-white">Not sure which service fits your property?</h2>
            <p className="mt-5 text-white/90 text-[17px] leading-[1.8] max-w-2xl">
              Contact us and we will help you identify the right scope, schedule, and price for your specific situation.
              All estimates are free and come with no obligation.
            </p>
          </div>
          <div className="lg:justify-self-end">
            <Link to="/contact" data-testid="services-cta-estimate" className="eco-btn"
              style={{ background: "#fff", color: "#2D6A4F" }}>
              Request a Free Estimate <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
